import React, { useState } from 'react';
import { Search, Plus, Edit, Trash2, Filter, Download, User, Calendar, Lightbulb } from 'lucide-react';
import './Notes.css';

const Notes = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNote, setSelectedNote] = useState(null);
  const [filterAuthor, setFilterAuthor] = useState('all');

  // Mock notes data - Study insights and observations
  const notes = [
    {
      id: 1,
      title: 'Seeing a Pattern in Dose Times',
      date: 'Aug 28, 2025, 8:45 PM',
      author: 'Thatiane Gellman',
      summary: 'Temporal adherence patterns across patient cohort',
      content: `Observed consistent patterns in medication timing across multiple patients in the Alzheimer's study. Morning doses (8-10 AM) show highest adherence rates at approximately 87%, while evening doses (6-8 PM) drop to 62% adherence.

Hypothesis: Evening cognitive decline (sundowning effect) may be contributing to reduced adherence. Patients with moderate Alzheimer's show more pronounced timing variations compared to early-stage patients.

Recommendation: Consider shifting evening doses to earlier afternoon (4-5 PM) for patients exhibiting sundowning symptoms. This could potentially improve overall adherence by 15-20% based on preliminary data.`,
      tags: ['adherence', 'patterns', 'timing', 'sundowning']
    },
    {
      id: 2,
      title: 'Idea: Play Music During Dosage',
      date: 'Aug 27, 2025, 2:30 PM',
      author: 'Dr. Smith',
      summary: 'Music therapy integration proposal',
      content: `Several patients have demonstrated improved medication compliance when familiar music is playing during dose times. Observing that auditory cues seem to improve memory recall and create positive associations with medication routine.

Proposed Study Extension: Implement personalized music playlists during scheduled dose times. Music selections should be from the patient's preferred era (typically 1950s-1970s for current cohort) to maximize recognition and positive response.

Initial observations show 23% improvement in on-time dosing when music cues are present. This aligns with existing research on music therapy benefits for Alzheimer's patients.`,
      tags: ['music-therapy', 'innovation', 'compliance', 'memory']
    },
    {
      id: 3,
      title: 'Clear Water Bottles Improve Monitoring',
      date: 'Aug 26, 2025, 10:15 AM',
      author: 'Nurse Johnson',
      summary: 'Visual verification enhancement',
      content: `Discovery: Patients using clear water bottles during medication intake provide significantly better visual verification of actual consumption. Can observe swallowing behavior and confirm pills are ingested rather than hidden or pocketed.

This observation came from reviewing video recordings of dose administration. Transparent bottles allow 99% certainty of adherence verification versus 73% with opaque containers.

Recommendation: Provide all study participants with standardized clear water bottles. This simple intervention could dramatically improve data quality without adding technological complexity.`,
      tags: ['monitoring', 'verification', 'best-practices']
    },
    {
      id: 4,
      title: 'Caregiver Presence Impact on Adherence',
      date: 'Aug 25, 2025, 4:20 PM',
      author: 'Dr. Williams',
      summary: 'Social support factors in medication compliance',
      content: `Analysis reveals strong correlation between caregiver presence during dose times and adherence rates. Patients with consistent caregiver support show 91% adherence versus 58% for those self-administering.

Interesting finding: The identity of the caregiver matters less than consistency. Whether family member, home health aide, or rotating caregivers, regular human presence is the key factor.

Consider: Can video calling or telehealth check-ins replicate this effect for patients with limited in-person support? Pilot program proposed for next quarter.`,
      tags: ['caregivers', 'social-factors', 'adherence', 'telehealth']
    },
    {
      id: 5,
      title: 'Device Battery Patterns and User Behavior',
      date: 'Aug 24, 2025, 11:30 AM',
      author: 'Thatiane Gellman',
      summary: 'Technical observations affecting study outcomes',
      content: `Correlation noted between low device battery levels and missed doses. When battery drops below 30%, adherence decreases by 18%. This appears related to reduced alarm volume and screen brightness rather than device failure.

Patients with moderate cognitive impairment are less likely to notice or respond to charging indicators. Early-stage patients typically maintain devices properly.

Action Items: Implement proactive charging reminders to caregivers at 40% battery. Consider adding backup battery packs to patient kits. Update study protocol to include weekly device checks.`,
      tags: ['device', 'technology', 'maintenance', 'protocol']
    },
    {
      id: 6,
      title: 'Morning Routine Integration Success',
      date: 'Aug 23, 2025, 3:45 PM',
      author: 'Dr. Chen',
      summary: 'Behavioral integration strategies',
      content: `Patients who integrate medication into existing morning routines (with coffee, after breakfast, etc.) show 34% better long-term adherence than those treating medication as separate activity.

Procedural memory appears more preserved in Alzheimer's patients than episodic memory. Leveraging established routines rather than creating new behaviors shows promising results.

Best practices observed: Link medication to strongest existing habit (usually coffee/breakfast), position device in same location as trigger activity, maintain consistent timing daily.`,
      tags: ['behavior', 'routines', 'memory', 'best-practices']
    },
    {
      id: 7,
      title: 'Seasonal Adherence Variations',
      date: 'Aug 22, 2025, 9:10 AM',
      author: 'Nurse Davis',
      summary: 'Environmental factors in study data',
      content: `Preliminary analysis suggests seasonal variations in adherence patterns. Summer months show 8% higher adherence than winter months. Possible factors: increased daylight hours, better sleep patterns, more social activity.

Winter decline particularly pronounced in December-February, possibly compounded by holiday disruptions and weather-related isolation. This could affect study outcome interpretations if not accounted for.

Recommendation: Normalize data for seasonal effects. Consider environmental enrichment interventions during winter months to maintain consistency.`,
      tags: ['seasonal', 'environmental', 'data-analysis']
    },
    {
      id: 8,
      title: 'Video Verification Limitations',
      date: 'Aug 21, 2025, 11:20 AM',
      author: 'Dr. Martinez',
      summary: 'Technical constraints and solutions',
      content: `Identified blind spots in video verification system. Camera angles sometimes miss actual pill ingestion, particularly for patients who turn away or have hand tremors that obscure view.

False positives occurring in ~12% of recorded doses where pills appear to be taken but were actually pocketed or dropped. This is critical for data integrity in clinical trial.

Proposed solutions: Add secondary camera angle, implement pill-counting reconciliation checks, use UV-marked pills for verification. Multi-modal verification may be necessary for high-confidence data.`,
      tags: ['video', 'verification', 'data-quality', 'technology']
    }
  ];

  const authors = ['all', ...new Set(notes.map(note => note.author))];

  const filteredNotes = notes.filter(note => {
    const matchesSearch = 
      note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.summary.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesAuthor = 
      filterAuthor === 'all' || note.author === filterAuthor;
    
    return matchesSearch && matchesAuthor;
  });

  return (
    <div className="notes-page">
      <div className="notes-header">
        <div className="header-content">
          <h1>Study Notes & Insights</h1>
          <p>Observations and insights from the current Alzheimer's clinical study</p>
        </div>
        <div className="header-actions">
          <button className="action-button primary">
            <Plus size={16} />
            New Note
          </button>
          <button className="action-button secondary">
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="notes-filters">
        <div className="search-filter">
          <Search className="search-icon" size={16} />
          <input
            type="text"
            placeholder="Search notes by title, author, or topic..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="filter-search"
          />
        </div>
        
        <div className="filter-group">
          <button className="filter-btn">
            <Filter size={16} />
            Filters
          </button>
          <select 
            value={filterAuthor}
            onChange={(e) => setFilterAuthor(e.target.value)}
            className="author-filter"
          >
            {authors.map(author => (
              <option key={author} value={author}>
                {author === 'all' ? 'All Authors' : author}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="notes-layout">
        <div className="notes-list">
          {filteredNotes.map((note) => (
            <div 
              key={note.id} 
              className={`note-card ${selectedNote?.id === note.id ? 'selected' : ''}`}
              onClick={() => setSelectedNote(note)}
            >
              <div className="note-card-header">
                <h3>{note.title}</h3>
                <div className="note-actions">
                  <button className="icon-btn" onClick={(e) => e.stopPropagation()}>
                    <Edit size={14} />
                  </button>
                  <button className="icon-btn" onClick={(e) => e.stopPropagation()}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <p className="note-summary">{note.summary}</p>
              <div className="note-meta">
                <div className="meta-item">
                  <User size={14} />
                  <span>{note.author}</span>
                </div>
                <div className="meta-item">
                  <Calendar size={14} />
                  <span>{note.date}</span>
                </div>
              </div>
              <div className="note-tags">
                {note.tags.map((tag, idx) => (
                  <span key={idx} className="tag">{tag}</span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="note-detail">
          {selectedNote ? (
            <>
              <div className="detail-header">
                <h2>{selectedNote.title}</h2>
                <div className="detail-actions">
                  <button className="icon-btn">
                    <Edit size={16} />
                  </button>
                  <button className="icon-btn">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="detail-meta">
                <div className="meta-row">
                  <strong>Author:</strong> {selectedNote.author}
                </div>
                <div className="meta-row">
                  <strong>Date:</strong> {selectedNote.date}
                </div>
              </div>
              <div className="detail-content">
                <h3>{selectedNote.summary}</h3>
                {selectedNote.content.split('\n\n').map((paragraph, idx) => (
                  <p key={idx}>{paragraph}</p>
                ))}
              </div>
            </>
          ) : (
            <div className="no-selection">
              <Lightbulb size={48} />
              <h3>Select a note</h3>
              <p>Choose a note from the list to view insights and observations</p>
            </div>
          )}
        </div>
      </div>

      {filteredNotes.length === 0 && (
        <div className="no-results">
          <Lightbulb size={48} />
          <h3>No notes found</h3>
          <p>Try adjusting your filters or search terms</p>
        </div>
      )}
    </div>
  );
};

export default Notes;

